import { useEffect, useState } from "react";
import { flagUrl } from "@/lib/team-flags";
import { useTeamLogo } from "@/lib/team-logos";
import { cn } from "@/lib/utils";

interface Props {
  team: string | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZES = {
  sm: { box: "h-5 w-7", logoBox: "h-5 w-5", img: 40, text: "text-[8px]" },
  md: { box: "h-6 w-9", logoBox: "h-6 w-6", img: 80, text: "text-[9px]" },
  lg: { box: "h-8 w-12", logoBox: "h-8 w-8", img: 160, text: "text-[11px]" },
} as const;

/** Two-letter shorthand used by the placeholder badge. */
const initials = (team: string | null | undefined) => {
  const words = (team ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/** Club crest when the league shows logos, otherwise the country flag, otherwise a placeholder badge. */
export const TeamFlag = ({ team, size = "md", className }: Props) => {
  const logo = useTeamLogo(team);
  const url = flagUrl(team, SIZES[size].img as 40 | 80 | 160);
  const [logoFailed, setLogoFailed] = useState(false);
  const [flagFailed, setFlagFailed] = useState(false);

  useEffect(() => setLogoFailed(false), [logo]);
  useEffect(() => setFlagFailed(false), [url]);

  if (logo && !logoFailed) {
    return (
      <span className={cn("inline-flex items-center justify-center shrink-0", SIZES[size].logoBox, className)}>
        <img
          src={logo}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
          onError={() => setLogoFailed(true)}
        />
      </span>
    );
  }

  const cls = cn(
    "inline-flex items-center justify-center overflow-hidden rounded-sm bg-muted shrink-0 ring-1 ring-border/60",
    SIZES[size].box,
    className,
  );

  if (!url || flagFailed) {
    return (
      <span className={cls} title={team ?? undefined} aria-hidden>
        <span className={cn("font-bold uppercase tracking-tight text-muted-foreground", SIZES[size].text)}>
          {initials(team)}
        </span>
      </span>
    );
  }

  return (
    <span className={cls}>
      <img
        src={url}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
        onError={() => setFlagFailed(true)}
      />
    </span>
  );
};
